import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useRef,
} from 'react';

import Box              from '@mui/material/Box';
import Button           from '@mui/material/Button';
import Grid             from '@mui/material/Grid';
import ButtonGroup      from '@mui/material/ButtonGroup';
import Modal            from '@mui/material/Modal';
import ModalBox         from 'components/Generic/MUI/ModalBox';

import grey                 from '@mui/material/colors/grey';
import CameraAlt            from '@mui/icons-material/CameraAlt';
import Replay               from '@mui/icons-material/Replay';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';

import { Map, Iterable } from 'immutable';
import isEmpty           from 'lodash/isEmpty';

import { isValidURL } from 'helpers/url';

import { Display }   from 'components';
import FileUpload    from 'components/Generic/Inputs/FileUpload';

function Picture({ name = null, value = null, onChange = null, disabled = false }) {
  const [state, setState] = useState(new Map());

  const snapPreview = useRef();
  const liveCamera  = useRef();
  const mainCanvas  = useRef();

  const isOpenCamera  = state.get('isOpenCamera', false);
  const isSnapped     = state.get('isSnapped', false);
  const isSaved       = state.get('isSaved', false);
  const mode          = state.get('mode', 'image');
  const video         = state.get('video', null);
  const videoStream   = state.get('videoStream', '');

  function setMode(md) {
    setState(state.set('mode', md));
  }

  function openCamera() {
    setState(state.set('isOpenCamera', true));
  }

  function closeCamera() {
    setState(state.set('isOpenCamera', false));
  }

  function snapPhoto() {
    setState(state.set('isSnapped', true).set('video', liveCamera.current));
  }

  function retakePhoto() {
    video.play();
    setState(state.set('isSnapped', false));
  }

  function savePhoto() {
    setState(state.set('isSaved', true));
  }

  function stopMediaRecording() {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  }

  function resetAll() {
    stopMediaRecording();

    setState(state.set('isSnapped', false).set('isOpenCamera', false).set('isSaved', false));
  }

  // https://stackoverflow.com/questions/12168909/blob-from-dataurl
  function dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    const blob = new Blob([ab], { type: mimeString });
    return blob;
  }

  useEffect(() => {
    if (isOpenCamera) {
      const constraints = { video: true };

      navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
        const videoGUM = liveCamera.current;
        videoGUM.srcObject = stream;

        setState(state.set('videoStream', stream));
      });
    } else {
      stopMediaRecording();
    }
  }, [isOpenCamera]);

  useEffect(() => {
    if (!isSnapped || isEmpty(video)) return;

    const canvas    = snapPreview.current;
    const context   = canvas.getContext('2d');
    canvas.width = video.videoWidth / 2;
    canvas.height = video.videoHeight / 2;

    context.drawImage(video, 0, 0, video.videoWidth / 2, video.videoHeight / 2);

    video.pause();
  }, [isSnapped]);

  useEffect(() => {
    if (!value) return;

    let url = '';
    if (!isEmpty(value.get('blob'))) {
      url = window.URL.createObjectURL(value.get('blob')[0]);
    } else if (isValidURL(value.get('url'))) {
      url = value.get('url');
    } else return;

    const canvas     = mainCanvas.current;
    const mainContext = canvas.getContext('2d');

    const img = new Image();
    img.onload = function(){
      mainContext.drawImage(img, 0, 0); // Or at whatever offset you like
    };

    img.src = url;
  }, [value, mainCanvas.current]);

  useEffect(() => {
    if (!isSaved) return;

    const snapCanvas = snapPreview.current;
    const canvas     = mainCanvas.current;

    canvas.width = video.videoWidth / 2;
    canvas.height = video.videoHeight / 2;

    const snapContext = snapCanvas.getContext('2d');
    const mainContext = canvas.getContext('2d');

    try {
      const image = snapContext.getImageData(0, 0, video.videoWidth / 2, video.videoWidth / 2);
      mainContext.putImageData(image, 0, 0);
    } catch (err) {
      // todo: show error message
    }

    const blob = dataURItoBlob(canvas.toDataURL('image/jpeg'));
    onChange(name, new Map({ blob: [blob] }));

    stopMediaRecording();
    closeCamera();
  }, [isSaved]);

  const hideVideo = isSnapped ? 'none' : 'block';

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <ButtonGroup color="primary" aria-label="primary button group">
          <Button variant={mode === 'image' ? 'contained' : 'outlined'} onClick={() => setMode('image')} data-testid="picture">{t('eos.landing_page.label.picture')}</Button>
          <Button variant={mode === 'file_upload' ? 'contained' : 'outlined'} onClick={() => setMode('file_upload')} data-testid="file-upload">{t('eos.landing_page.label.file_upload')}</Button>
        </ButtonGroup>
      </Grid>
      <Display if={mode === 'image'}>
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex', flexDirection: 'column', minHeight: 100, width: '100%', border: 1, borderRadius: 1, justifyContent: 'center', alignItems: 'center', mt: 2, borderColor: grey[400], p: 2,
          }}
          >
            <Display if={isSaved || value}>
            <canvas ref={mainCanvas} data-testid="main-canvas" style={{ maxWidth: '100%', maxHeight: '240px' }} />
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
                <Button variant="outlined" color={isSnapped ? 'error' : 'primary'} onClick={() => resetAll()} startIcon={<Replay />} sx={{ display: 'flex' }} data-testid="retake">{t('eos.landing_page.label.retake_picture')}</Button>
              </Grid>
            </Display>
            <Display if={!isSaved}>
              <Button variant="contained" color="primary" disabled={disabled} onClick={() => openCamera()} startIcon={<CameraAlt />} sx={{ display: 'flex' }} data-testid="start-camera">{t('eos.landing_page.label.start_camera')}</Button>
            </Display>
          </Box>
        </Grid>
      </Display>
      <Display if={mode === 'file_upload'}>
        <FileUpload
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      </Display>
      <Modal
        open={isOpenCamera}
        onClose={() => closeCamera()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ModalBox>
          <Box sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          }}
          >
            <video ref={liveCamera} playsInline autoPlay muted style={{ display: hideVideo, width: '100%', maxHeight: '240px' }} data-testid="camera" />
            <Display if={!isSnapped}>
              <Button variant="outlined" color="primary" onClick={() => snapPhoto()} startIcon={<VideoCameraFrontIcon />} sx={{ mt: 2 }} data-testid="snap">{t('eos.landing_page.label.snap_photo')}</Button>
            </Display>
            <Display if={isSnapped}>
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <canvas ref={snapPreview} height="240" />
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Button variant="outlined" color="primary" onClick={() => savePhoto()} startIcon={<VideoCameraFrontIcon />} sx={{ mt: 2, mb: 1 }} data-testid="save">{t('eos.landing_page.label.save')}</Button>
                  <Button variant="outlined" color={isSnapped ? 'error' : 'primary'} onClick={() => retakePhoto()} startIcon={<Replay />} sx={{ display: 'flex' }} data-testid="retake">{t('eos.landing_page.label.retake_picture')}</Button>
                </Grid>
              </Grid>
            </Display>
          </Box>
        </ModalBox>
      </Modal>
    </Grid>
  );
}

Picture.propTypes = {
  name:     PropTypes.string,
  value:    PropTypes.objectOf(Iterable),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default Picture;
